import React, { useEffect, useRef } from 'react';

interface VoiceVisualizerProps {
  isActive: boolean;
  type: 'user' | 'assistant';
}

export const VoiceVisualizer: React.FC<VoiceVisualizerProps> = ({ isActive, type }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const amplitudeRef = useRef<number>(0);
  const targetAmplitudeRef = useRef<number>(0);

  useEffect(() => {
    targetAmplitudeRef.current = isActive ? 1 : 0;
  }, [isActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    let time = 0;

    const animate = () => {
      // Smooth amplitude transition
      amplitudeRef.current += (targetAmplitudeRef.current - amplitudeRef.current) * 0.1;

      ctx.clearRect(0, 0, width, height);

      const centerY = height / 2;
      const baseAmplitude = 10;
      const maxAmplitude = 30;
      const amplitude = baseAmplitude + (maxAmplitude - baseAmplitude) * amplitudeRef.current;
      
      // Draw waveform
      const numBars = type === 'user' ? 5 : 7;
      const barWidth = 3;
      const spacing = 8;
      const totalWidth = numBars * barWidth + (numBars - 1) * spacing;
      const startX = (width - totalWidth) / 2;

      ctx.fillStyle = type === 'user' 
        ? getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim() || '#3b82f6'
        : getComputedStyle(document.documentElement).getPropertyValue('--color-success').trim() || '#10b981';

      for (let i = 0; i < numBars; i++) {
        const x = startX + i * (barWidth + spacing);
        const offset = i * 0.5;
        const barHeight = amplitude * Math.abs(Math.sin(time + offset));
        
        // Draw bar (centered vertically)
        ctx.fillRect(
          x,
          centerY - barHeight / 2,
          barWidth,
          barHeight
        );
      }

      time += 0.15;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [type]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-12"
      style={{ width: '100%', height: '48px' }}
    />
  );
};

